import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { Helmet } from "react-helmet";
import Swal from "sweetalert2";
import "../style/styles.css";
import "../style/demo.css";

const PRODUCT_OPTIONS = {
  "PMS Software (HotelSof)": {
    heroTitle: "HotelSof PMS Demo",
    metaDescription:
      "Request a demo for PMS Software HotelSof by EBS Software Solution. Submit your details, verify your email and receive demo access details.",
    companyLabel: "Hotel / Company Name",
    companyPlaceholder: "Enter hotel or company name",
    messagePlaceholder:
      "Write requirement, number of rooms, property type, or preferred demo details",
    demoUrl: "",
  },
  "eRestaurSof (POS for Restaurant)": {
    heroTitle: "eRestaurSof Restaurant POS Demo",
    metaDescription:
      "Request a demo for eRestaurSof POS for Restaurant by EBS Software Solution. Submit your details, verify your email and receive demo access details.",
    companyLabel: "Restaurant / Company Name",
    companyPlaceholder: "Enter restaurant or company name",
    messagePlaceholder:
      "Write requirement, number of outlets, restaurant type, or preferred demo details",
    demoUrl: "https://ehotelsof.ebssoftware.in",
  },
  "eTeaSof (Tea Garden Management System)": {
    heroTitle: "eTeaSof Tea Garden Management System Demo",
    metaDescription:
      "Request a demo for eTeaSof Tea Garden Management System by EBS Software Solution for tea garden operations, worker management, attendance, wages, stores, inventory and management reporting.",
    companyLabel: "Tea Garden / Company Name",
    companyPlaceholder: "Enter tea garden or company name",
    messagePlaceholder:
      "Write your tea garden requirement, number of divisions/gardens, workers, payroll, attendance, stores, inventory or reporting requirements",
    demoUrl: "https://ebssoftware.co.in/teagarden/",
  },
};

const INITIAL_FORM = {
  product: "PMS Software (HotelSof)",
  customerName: "",
  companyName: "",
  phone: "",
  email: "",
  city: "",
  preferredDate: "",
  preferredTime: "",
  message: "",
  website: "", // hidden anti-spam honeypot
  captchaAnswer: "",
};

const INITIAL_LOCATION = {
  loading: false,
  error: "",
  ip: "",
  country: "",
  state: "",
  city: "",
  latitude: "",
  longitude: "",
  accuracy: "",
  source: "not_detected",
};

/*
  Local React testing:
  Create .env in project root if PHP API is on live hosting:
  REACT_APP_DEMO_API_BASE_URL=https://ebssoftware.co.in
*/
const API_BASE_URL = (
  process.env.REACT_APP_DEMO_API_BASE_URL || "https://ebssoftware.co.in"
).replace(/\/$/, "");

const CAPTCHA_API = `${API_BASE_URL}/api/contact-captcha.php`;
const DEMO_REQUEST_API = `${API_BASE_URL}/api/demo-request.php`;
const DEMO_LOCATION_API = `${API_BASE_URL}/api/demo-location.php`;
const DEMO_REVERSE_LOCATION_API = `${API_BASE_URL}/api/demo-reverse-location.php`;

const Demo = () => {
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [emailStatus, setEmailStatus] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [captcha, setCaptcha] = useState({
    question: "",
    token: "",
    loading: false,
    error: "",
  });
  const [visitorLocation, setVisitorLocation] = useState(INITIAL_LOCATION);

  const selectedProduct =
    PRODUCT_OPTIONS[formData.product] || PRODUCT_OPTIONS[INITIAL_FORM.product];

  const today = useMemo(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }, []);

  const readApiResponse = useCallback(async (response, fallbackMessage) => {
    const responseText = await response.text();
    const trimmedText = responseText.trim();
    const contentType = response.headers.get("content-type") || "";

    if (!trimmedText) {
      return {
        success: false,
        message: `${fallbackMessage} Server returned empty response. HTTP Status: ${response.status}`,
      };
    }

    if (contentType.includes("application/json") || trimmedText.startsWith("{")) {
      try {
        return JSON.parse(trimmedText);
      } catch (error) {
        return {
          success: false,
          message: `${fallbackMessage} Invalid JSON received from server. HTTP Status: ${response.status}`,
          raw: trimmedText.substring(0, 500),
        };
      }
    }

    const lowerText = trimmedText.toLowerCase();
    const looksLikeHtml =
      lowerText.startsWith("<!doctype") || lowerText.startsWith("<html");

    return {
      success: false,
      message: looksLikeHtml
        ? `${fallbackMessage} API returned HTML instead of JSON. Please upload the PHP files inside public_html/api/.`
        : `${fallbackMessage} Invalid server response. HTTP Status: ${response.status}`,
      raw: trimmedText.substring(0, 500),
    };
  }, []);

  const fillCityFromLocation = useCallback((cityName, forceReplace = false) => {
    if (!cityName) {
      return;
    }

    setFormData((previous) => ({
      ...previous,
      city:
        forceReplace || !previous.city.trim()
          ? cityName
          : previous.city,
    }));
  }, []);

  const loadCaptcha = useCallback(async () => {
    setCaptcha((previous) => ({
      ...previous,
      loading: true,
      error: "",
    }));

    try {
      const response = await fetch(CAPTCHA_API, {
        method: "GET",
        headers: { Accept: "application/json" },
        cache: "no-store",
      });

      const result = await readApiResponse(response, "Captcha loading failed.");

      if (!response.ok || !result.success) {
        throw new Error(result.message || "Unable to load captcha.");
      }

      setCaptcha({
        question: result.question || "",
        token: result.token || "",
        loading: false,
        error: "",
      });

      setFormData((previous) => ({
        ...previous,
        captchaAnswer: "",
      }));
    } catch (error) {
      setCaptcha({
        question: "",
        token: "",
        loading: false,
        error:
          error.message ||
          "Captcha loading failed. Please refresh the page and try again.",
      });
    }
  }, [readApiResponse]);

  const reverseLocationByBrowserApi = useCallback(async (latitude, longitude) => {
    const url =
      `https://api.bigdatacloud.net/data/reverse-geocode-client` +
      `?latitude=${encodeURIComponent(latitude)}` +
      `&longitude=${encodeURIComponent(longitude)}` +
      `&localityLanguage=en`;

    const response = await fetch(url, {
      method: "GET",
      headers: { Accept: "application/json" },
      cache: "no-store",
    });

    if (!response.ok) {
      throw new Error("Browser reverse location lookup failed.");
    }

    const data = await response.json();

    return {
      success: true,
      country: data.countryName || "",
      state: data.principalSubdivision || "",
      city: data.city || data.locality || data.localityInfo?.administrative?.[3]?.name || "",
      source: "browser_geolocation_bigdatacloud",
    };
  }, []);

  const reverseLocationByServer = useCallback(
    async (latitude, longitude, accuracy) => {
      const response = await fetch(DEMO_REVERSE_LOCATION_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        cache: "no-store",
        body: JSON.stringify({
          latitude,
          longitude,
          accuracy,
        }),
      });

      const result = await readApiResponse(
        response,
        "Latitude/longitude location lookup failed."
      );

      if (!response.ok || !result.success) {
        throw new Error(result.message || "Latitude/longitude lookup failed.");
      }

      return result;
    },
    [readApiResponse]
  );

  const detectBrowserLocation = useCallback(
    async (showSuccessMessage = false) => {
      if (!navigator.geolocation) {
        setVisitorLocation((previous) => ({
          ...previous,
          loading: false,
          error: "Your browser does not support GPS/location detection.",
        }));
        return;
      }

      setVisitorLocation((previous) => ({
        ...previous,
        loading: true,
        error: "",
      }));

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const latitude = Number(position.coords.latitude).toFixed(6);
          const longitude = Number(position.coords.longitude).toFixed(6);
          const accuracy = position.coords.accuracy
            ? Math.round(position.coords.accuracy).toString()
            : "";

          try {
            let result;

            try {
              result = await reverseLocationByServer(latitude, longitude, accuracy);
            } catch (serverError) {
              result = await reverseLocationByBrowserApi(latitude, longitude);
            }

            const nextLocation = {
              loading: false,
              error: "",
              ip: result.ip || "",
              country: result.country || "",
              state: result.state || "",
              city: result.city || "",
              latitude,
              longitude,
              accuracy,
              source: result.source || "browser_geolocation",
            };

            setVisitorLocation((previous) => ({
              ...nextLocation,
              ip: previous.ip || nextLocation.ip,
            }));
            fillCityFromLocation(nextLocation.city, true);

            if (showSuccessMessage) {
              Swal.fire({
                icon: "success",
                title: "Location Detected",
                text:
                  [nextLocation.city, nextLocation.state, nextLocation.country]
                    .filter(Boolean)
                    .join(", ") || "Location checked successfully.",
                confirmButtonText: "OK",
              });
            }
          } catch (error) {
            setVisitorLocation((previous) => ({
              ...previous,
              loading: false,
              latitude,
              longitude,
              accuracy,
              source: "browser_geolocation_latlong_only",
              error:
                error.message ||
                "Latitude and longitude detected, but state/country could not be found.",
            }));
          }
        },
        (error) => {
          let message = "Unable to detect location from browser.";

          if (error.code === 1) {
            message = "Location permission was denied. You can still submit the form.";
          } else if (error.code === 2) {
            message = "Location is unavailable. Please try again.";
          } else if (error.code === 3) {
            message = "Location detection timed out. Please try again.";
          }

          setVisitorLocation((previous) => ({
            ...previous,
            loading: false,
            error: message,
          }));
        },
        {
          enableHighAccuracy: true,
          timeout: 12000,
          maximumAge: 60000,
        }
      );
    },
    [fillCityFromLocation, reverseLocationByBrowserApi, reverseLocationByServer]
  );

  const loadVisitorLocation = useCallback(async () => {
    setVisitorLocation((previous) => ({
      ...previous,
      loading: true,
      error: "",
    }));

    try {
      const response = await fetch(DEMO_LOCATION_API, {
        method: "GET",
        headers: { Accept: "application/json" },
        cache: "no-store",
      });

      const result = await readApiResponse(response, "Location loading failed.");

      if (!response.ok || !result.success) {
        throw new Error(result.message || "Unable to detect IP location.");
      }

      const nextLocation = {
        loading: false,
        error: "",
        ip: result.ip || "",
        country: result.country || "",
        state: result.state || "",
        city: result.city || "",
        latitude: "",
        longitude: "",
        accuracy: "",
        source: result.source || "ip_lookup",
      };

      setVisitorLocation(nextLocation);
      fillCityFromLocation(nextLocation.city, false);

      /*
        IP location is only approximate and can show the ISP gateway city.
        Always request browser/device location after the IP lookup. When the
        customer allows permission, GPS-derived city/state/country replaces
        the approximate IP location and also refreshes the City field.
      */
      detectBrowserLocation(false);
    } catch (error) {
      setVisitorLocation((previous) => ({
        ...previous,
        loading: false,
        error:
          error.message ||
          "Unable to detect IP location. Please use device location or submit the form manually.",
      }));

      detectBrowserLocation(false);
    }
  }, [detectBrowserLocation, fillCityFromLocation, readApiResponse]);

  useEffect(() => {
    loadCaptcha();
    loadVisitorLocation();
  }, [loadCaptcha, loadVisitorLocation]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((previous) => ({
      ...previous,
      [name]: value,
    }));

    setEmailStatus("");
  };

  const validateForm = () => {
    const product = formData.product.trim();
    const customerName = formData.customerName.trim();
    const companyName = formData.companyName.trim();
    const phone = formData.phone.trim();
    const email = formData.email.trim();
    const city = formData.city.trim();
    const preferredDate = formData.preferredDate.trim();
    const preferredTime = formData.preferredTime.trim();
    const captchaAnswer = formData.captchaAnswer.trim();

    if (
      !product ||
      !customerName ||
      !companyName ||
      !phone ||
      !email ||
      !city ||
      !preferredDate ||
      !preferredTime
    ) {
      Swal.fire({
        icon: "warning",
        title: "Required fields missing",
        text:
          "Please enter Product, Customer Name, Hotel/Company Name, Mobile No., Email, City, Preferred Date and Preferred Time.",
        confirmButtonText: "OK",
      });
      return false;
    }

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailPattern.test(email)) {
      Swal.fire({
        icon: "warning",
        title: "Invalid email address",
        text: "Please enter a valid email address.",
        confirmButtonText: "OK",
      });
      return false;
    }

    const mobilePattern = /^[0-9+\-\s()]{7,20}$/;

    if (!mobilePattern.test(phone)) {
      Swal.fire({
        icon: "warning",
        title: "Invalid mobile number",
        text: "Please enter a valid mobile number.",
        confirmButtonText: "OK",
      });
      return false;
    }

    if (!captcha.token || !captcha.question) {
      Swal.fire({
        icon: "warning",
        title: "Human verification missing",
        text: captcha.error || "Please reload the captcha and try again.",
        confirmButtonText: "OK",
      });
      return false;
    }

    if (!captchaAnswer) {
      Swal.fire({
        icon: "warning",
        title: "Human verification required",
        text: "Please answer the captcha question.",
        confirmButtonText: "OK",
      });
      return false;
    }

    if (!/^\d+$/.test(captchaAnswer)) {
      Swal.fire({
        icon: "warning",
        title: "Invalid captcha answer",
        text: "Captcha answer should contain numbers only.",
        confirmButtonText: "OK",
      });
      return false;
    }

    return true;
  };

  const submitForm = async (event) => {
    event.preventDefault();

    if (isSending) {
      return;
    }

    if (!validateForm()) {
      return;
    }

    setIsSending(true);
    setEmailStatus("");

    try {
      const response = await fetch(DEMO_REQUEST_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        cache: "no-store",
        body: JSON.stringify({
          product: formData.product.trim(),
          customerName: formData.customerName.trim(),
          companyName: formData.companyName.trim(),
          phone: formData.phone.trim(),
          email: formData.email.trim(),
          city: formData.city.trim(),
          preferredDate: formData.preferredDate.trim(),
          preferredTime: formData.preferredTime.trim(),
          message: formData.message.trim(),
          website: formData.website.trim(),
          captcha_token: captcha.token,
          captcha_answer: formData.captchaAnswer.trim(),
          visitor_ip: visitorLocation.ip,
          visitor_country: visitorLocation.country,
          visitor_state: visitorLocation.state,
          visitor_city: visitorLocation.city,
          visitor_latitude: visitorLocation.latitude,
          visitor_longitude: visitorLocation.longitude,
          visitor_accuracy: visitorLocation.accuracy,
          visitor_location_source: visitorLocation.source,
          demo_url: selectedProduct.demoUrl,
        }),
      });

      const result = await readApiResponse(
        response,
        "Unable to submit demo request."
      );

      if (!response.ok || !result.success) {
        if (
          (result.message || "").toLowerCase().includes("captcha") ||
          (result.message || "").toLowerCase().includes("verification")
        ) {
          loadCaptcha();
        }

        throw new Error(
          result.message || "Unable to submit demo request. Please try again."
        );
      }

      setEmailStatus("success");
      setFormData(INITIAL_FORM);
      loadCaptcha();
      loadVisitorLocation();

      Swal.fire({
        icon: "success",
        title: "Verification Email Sent",
        text:
          result.message ||
          "Please check your email and verify it. After verification, the demo link will be sent to your email.",
        confirmButtonText: "OK",
      });
    } catch (error) {
      console.error("Demo request error:", error);

      setEmailStatus("error");

      Swal.fire({
        icon: "error",
        title: "Demo Request Failed",
        text:
          error.message ||
          "Unable to submit demo request. Please try again or email us directly at info@ebssoftware.co.in.",
        confirmButtonText: "OK",
      });
    } finally {
      setIsSending(false);
    }
  };

  const handleProductDownload = () => {
    if (!selectedProduct.downloadUrl) {
      Swal.fire({
        icon: "info",
        title: "Download Not Available",
        text: "The download file is not configured for this product yet.",
        confirmButtonText: "OK",
      });
      return;
    }

    const downloadUrl = selectedProduct.downloadUrl.startsWith("http")
      ? selectedProduct.downloadUrl
      : `${window.location.origin}${selectedProduct.downloadUrl}`;

    window.location.href = downloadUrl;
  };

  const locationText = visitorLocation.loading
    ? "Detecting location..."
    : visitorLocation.country || visitorLocation.state || visitorLocation.city
    ? [visitorLocation.city, visitorLocation.state, visitorLocation.country]
        .filter(Boolean)
        .join(", ")
    : "Location not detected";

  const locationSourceText = visitorLocation.source.startsWith("browser_geolocation")
    ? "Device GPS location"
    : visitorLocation.source === "ip_lookup"
    ? "Approximate IP location"
    : "Location source unavailable";

  return (
    <main className="ebs-demo-page">
      <Helmet>
        <meta charSet="utf-8" />
        <title>Request for Demo | EBS Software Solution</title>
        <meta
          name="description"
          content={selectedProduct.metaDescription}
        />
      </Helmet>

      <section className="ebs-demo-hero ebs-demo-hero-compact">
        <Container>
          <div className="ebs-demo-hero-box">
            <span className="ebs-demo-badge">Request for Demo</span>
            <h1>{selectedProduct.heroTitle}</h1>
            <p>
              Submit your details and verify your email. The demo link and login
              details will be sent after verification.
            </p>
          </div>
        </Container>
      </section>

      <section className="ebs-demo-form-section">
        <Container>
          <Row className="justify-content-center">
            <Col xl={10}>
              <div className="ebs-demo-form-card">
                <div className="ebs-demo-form-heading">
                  <span>Book Product Demo</span>
                  <h2>Request Demo</h2>
                  <p>Please fill up the form below. Fields marked * are required.</p>
                </div>

                <div className="ebs-demo-location-card">
                  <div>
                    <span>Detected Location</span>
                    <strong>{locationText}</strong>
                    <small>{locationSourceText}</small>
                    {visitorLocation.ip && <small>IP: {visitorLocation.ip}</small>}
                    {visitorLocation.error && <small>{visitorLocation.error}</small>}
                  </div>
                  <div className="ebs-demo-location-actions">
                    <button
                      type="button"
                      className="ebs-demo-location-refresh"
                      onClick={loadVisitorLocation}
                      disabled={visitorLocation.loading || isSending}
                    >
                      Refresh IP
                    </button>
                    <button
                      type="button"
                      className="ebs-demo-location-gps"
                      onClick={() => detectBrowserLocation(true)}
                      disabled={visitorLocation.loading || isSending}
                    >
                      Use Device Location
                    </button>
                  </div>
                </div>

                {emailStatus === "success" && (
                  <div className="ebs-demo-alert ebs-demo-alert-success">
                    Verification email sent successfully. Please check your inbox.
                  </div>
                )}

                {emailStatus === "error" && (
                  <div className="ebs-demo-alert ebs-demo-alert-error">
                    Demo request failed. Please check the error message and try again.
                  </div>
                )}

                {captcha.error !== "" && (
                  <div className="ebs-demo-alert ebs-demo-alert-error">
                    {captcha.error}
                  </div>
                )}

                <form onSubmit={submitForm} noValidate>
                  <input
                    type="text"
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    tabIndex="-1"
                    autoComplete="off"
                    style={{
                      position: "absolute",
                      left: "-9999px",
                      height: 0,
                      width: 0,
                      opacity: 0,
                    }}
                    aria-hidden="true"
                  />

                  <Row className="g-3">
                    <Col md={12}>
                      <label className="form-label" htmlFor="product">
                        Choose Product <span>*</span>
                      </label>
                      <select
                        id="product"
                        name="product"
                        className="form-select ebs-demo-input"
                        value={formData.product}
                        onChange={handleChange}
                        required
                      >
                        {Object.keys(PRODUCT_OPTIONS).map((productName) => (
                          <option key={productName} value={productName}>
                            {productName}
                          </option>
                        ))}
                      </select>
                    </Col>

                    <Col md={6}>
                      <label className="form-label" htmlFor="customerName">
                        Customer Name <span>*</span>
                      </label>
                      <input
                        type="text"
                        id="customerName"
                        name="customerName"
                        className="form-control ebs-demo-input"
                        value={formData.customerName}
                        onChange={handleChange}
                        placeholder="Enter customer name"
                        autoComplete="name"
                        required
                      />
                    </Col>

                    <Col md={6}>
                      <label className="form-label" htmlFor="companyName">
                        {selectedProduct.companyLabel} <span>*</span>
                      </label>
                      <input
                        type="text"
                        id="companyName"
                        name="companyName"
                        className="form-control ebs-demo-input"
                        value={formData.companyName}
                        onChange={handleChange}
                        placeholder={selectedProduct.companyPlaceholder}
                        required
                      />
                    </Col>

                    <Col md={6}>
                      <label className="form-label" htmlFor="phone">
                        Mobile Number <span>*</span>
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        className="form-control ebs-demo-input"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+91 98765 43210"
                        autoComplete="tel"
                        required
                      />
                    </Col>

                    <Col md={6}>
                      <label className="form-label" htmlFor="email">
                        Email Address <span>*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        className="form-control ebs-demo-input"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="customer@example.com"
                        autoComplete="email"
                        required
                      />
                    </Col>

                    <Col md={4}>
                      <label className="form-label" htmlFor="city">
                        City <span>*</span>
                      </label>
                      <input
                        type="text"
                        id="city"
                        name="city"
                        className="form-control ebs-demo-input"
                        value={formData.city}
                        onChange={handleChange}
                        placeholder="Enter city"
                        required
                      />
                    </Col>

                    <Col md={4}>
                      <label className="form-label" htmlFor="preferredDate">
                        Preferred Date <span>*</span>
                      </label>
                      <input
                        type="date"
                        id="preferredDate"
                        name="preferredDate"
                        className="form-control ebs-demo-input"
                        value={formData.preferredDate}
                        onChange={handleChange}
                        min={today}
                        required
                      />
                    </Col>

                    <Col md={4}>
                      <label className="form-label" htmlFor="preferredTime">
                        Preferred Time <span>*</span>
                      </label>
                      <input
                        type="time"
                        id="preferredTime"
                        name="preferredTime"
                        className="form-control ebs-demo-input"
                        value={formData.preferredTime}
                        onChange={handleChange}
                        required
                      />
                    </Col>

                    <Col md={6}>
                      <label className="form-label">Detected State</label>
                      <input
                        type="text"
                        className="form-control ebs-demo-input ebs-demo-readonly"
                        value={visitorLocation.state || "Not detected"}
                        readOnly
                      />
                    </Col>

                    <Col md={6}>
                      <label className="form-label">Detected Country</label>
                      <input
                        type="text"
                        className="form-control ebs-demo-input ebs-demo-readonly"
                        value={visitorLocation.country || "Not detected"}
                        readOnly
                      />
                    </Col>

                    <Col md={12}>
                      <label className="form-label" htmlFor="message">
                        Requirement / Message
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        className="form-control ebs-demo-input ebs-demo-textarea"
                        rows="4"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder={selectedProduct.messagePlaceholder}
                      ></textarea>
                    </Col>

                    <Col md={12}>
                      <div className="ebs-demo-captcha-box">
                        <div className="ebs-demo-captcha-question-wrap">
                          <div>
                            <label className="form-label" htmlFor="captchaAnswer">
                              Human Verification <span>*</span>
                            </label>
                            <div className="ebs-demo-captcha-question">
                              {captcha.loading
                                ? "Loading captcha..."
                                : captcha.question || "Captcha not loaded"}
                            </div>
                          </div>

                          <button
                            type="button"
                            className="ebs-demo-captcha-refresh"
                            onClick={loadCaptcha}
                            disabled={captcha.loading || isSending}
                          >
                            Refresh
                          </button>
                        </div>

                        <input
                          className="form-control ebs-demo-input"
                          type="text"
                          id="captchaAnswer"
                          name="captchaAnswer"
                          value={formData.captchaAnswer}
                          onChange={handleChange}
                          placeholder="Enter answer"
                          inputMode="numeric"
                          autoComplete="off"
                          required
                        />
                      </div>
                    </Col>

                    <Col md={12}>
                      <div className="d-flex flex-column flex-md-row gap-2">
                        <button
                          type="submit"
                          className="ebs-demo-submit-btn flex-fill"
                          disabled={isSending || captcha.loading || !captcha.token}
                        >
                          {isSending ? "Submitting Request..." : "Submit Demo Request"}
                        </button>
                      </div>
                    </Col>
                  </Row>
                </form>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </main>
  );
};

export default Demo;
